import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Check,
  ClipboardCheck,
  MessageSquare,
  Send,
  ShieldCheck,
  Tag,
  Trash2,
  X,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../../lib/api'
import { useToast } from '../../ui/ToastProvider'
import {
  DialogBackdrop,
  DialogBody,
  DialogHeader,
  DialogPanel,
} from '../../ui/DialogLayout'

const REVIEW_LABELS = {
  PENDING: '검토 중',
  APPROVED: '승인',
  REJECTED: '반려',
}

const SCAN_LABELS = {
  VERIFIED: '악성 파일 검사 완료',
  PENDING: '악성 파일 검사 중',
  THREAT: '위험 파일 감지',
  UNSUPPORTED: '검사할 수 없는 파일',
  ACCESS_DENIED: '악성 파일 검사 실패',
  FAILED: '악성 파일 검사 실패',
  NOT_SCANNED: '악성 파일 검사 안 함',
  UNKNOWN: '확인 대기',
}

const SECURITY_LABELS = {
  VERIFIED: '파일 무결성 확인',
  UNKNOWN: '확인 대기',
  CLEAR: '민감정보 없음',
  DETECTED: '민감정보 확인 필요',
  CLEAN: '민감정보 없음',
  SENSITIVE: '민감정보 확인 필요',
  NOT_SCANNED: '민감정보 확인 전',
}

function unwrap(response, fallback) {
  return response?.data?.data ?? response?.data ?? fallback
}

function IconButton({ title, children, ...props }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40"
      {...props}
    >
      {children}
    </button>
  )
}

export default function OrgLibraryCollaborationPanel({
  apiRoot,
  rootOrgId,
  file,
  onClose,
}) {
  const { show } = useToast()
  const [details, setDetails] = useState(null)
  const [members, setMembers] = useState([])
  const [activeTab, setActiveTab] = useState('COMMENTS')
  const [comment, setComment] = useState('')
  const [mentionedIds, setMentionedIds] = useState([])
  const [mentionTarget, setMentionTarget] = useState('')
  const [tagText, setTagText] = useState('')
  const [reviewTarget, setReviewTarget] = useState('')
  const [reviewMessage, setReviewMessage] = useState('')
  const [decisionMessage, setDecisionMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [detailResponse, memberResponse] = await Promise.all([
        api.get(`${apiRoot}/workspaces/${rootOrgId}/files/${file.id}/collaboration`, {
          __silent: true, __suppressErrorPageRedirect: true,
        }),
        api.get(`${apiRoot}/workspaces/${rootOrgId}/member-targets`, {
          __silent: true, __suppressErrorPageRedirect: true,
        }),
      ])
      const nextDetails = unwrap(detailResponse, null)
      setDetails(nextDetails)
      setTagText((nextDetails?.tags || []).join(', '))
      setMembers(unwrap(memberResponse, []))
    } catch (error) {
      show(getApiErrorMessage(error, '파일 협업 정보를 불러오지 못했습니다.'), 'error')
      onClose()
    } finally {
      setLoading(false)
    }
  }, [apiRoot, file.id, onClose, rootOrgId, show])

  useEffect(() => { load() }, [load])

  const mentionedMembers = useMemo(
    () => members.filter((member) => mentionedIds.includes(member.principalId)),
    [members, mentionedIds],
  )

  const run = async (request, successMessage) => {
    setBusy(true)
    try {
      await request()
      if (successMessage) show(successMessage, 'success')
      await load()
      return true
    } catch (error) {
      show(getApiErrorMessage(error, '작업을 완료하지 못했습니다.'), 'error')
      return false
    } finally {
      setBusy(false)
    }
  }

  const addComment = async (event) => {
    event.preventDefault()
    if (!comment.trim()) return
    const succeeded = await run(
      () => api.post(`${apiRoot}/workspaces/${rootOrgId}/files/${file.id}/comments`, {
        body: comment.trim(),
        mentionedUserIds: mentionedIds,
      }),
      '댓글을 등록했습니다.',
    )
    if (succeeded) {
      setComment('')
      setMentionedIds([])
    }
  }

  const saveTags = async () => {
    const tags = tagText.split(',').map((value) => value.trim()).filter(Boolean)
    await run(
      () => api.put(`${apiRoot}/workspaces/${rootOrgId}/files/${file.id}/tags`, { tags }),
      '태그를 저장했습니다.',
    )
  }

  const requestReview = async () => {
    if (!reviewTarget) return
    const succeeded = await run(
      () => api.post(`${apiRoot}/workspaces/${rootOrgId}/files/${file.id}/reviews`, {
        assignedToUserId: Number(reviewTarget),
        message: reviewMessage.trim() || null,
      }),
      '검토를 요청했습니다.',
    )
    if (succeeded) {
      setReviewTarget('')
      setReviewMessage('')
    }
  }

  const decide = async (decision) => {
    if (!details?.review) return
    const succeeded = await run(
      () => api.post(
        `${apiRoot}/workspaces/${rootOrgId}/files/${file.id}/reviews/${details.review.id}/decision`,
        { decision, message: decisionMessage.trim() || null },
      ),
      decision === 'APPROVED' ? '검토를 승인했습니다.' : '검토를 반려했습니다.',
    )
    if (succeeded) setDecisionMessage('')
  }

  const tabs = [
    { key: 'COMMENTS', label: '댓글', icon: MessageSquare },
    { key: 'TAGS', label: '태그', icon: Tag },
    { key: 'REVIEW', label: '검토', icon: ClipboardCheck },
    { key: 'SECURITY', label: '보안', icon: ShieldCheck },
  ]

  return (
    <DialogBackdrop onMouseDown={(event) => event.target === event.currentTarget && !busy && onClose()}>
      <DialogPanel className="max-w-3xl" aria-labelledby="org-library-collaboration-title">
        <DialogHeader>
          <div className="min-w-0">
            <h2 id="org-library-collaboration-title" className="font-semibold text-slate-900">파일 정보</h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">{file.name}</p>
          </div>
          <IconButton title="닫기" onClick={onClose}><X className="h-4 w-4" /></IconButton>
        </DialogHeader>
        <div className="flex overflow-x-auto border-b border-slate-200 px-4">
          {tabs.map(({ key, label, icon: TabIcon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`inline-flex h-11 shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-medium ${activeTab === key ? 'border-cyan-600 text-cyan-800' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            >
              <TabIcon className="h-4 w-4" />{label}
            </button>
          ))}
        </div>
        <DialogBody className="min-h-[430px]">
          {loading ? (
            <div className="flex min-h-80 items-center justify-center text-sm text-slate-500">불러오는 중...</div>
          ) : activeTab === 'COMMENTS' ? (
            <div className="flex min-h-[390px] flex-col">
              <ol className="flex-1 divide-y divide-slate-100">
                {(details?.comments || []).map((value) => (
                  <li key={value.id} className="flex gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <strong className="text-sm text-slate-800">{value.authorName}</strong>
                        <time className="text-xs text-slate-400">{new Date(value.createdAt).toLocaleString('ko-KR')}</time>
                      </div>
                      <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-700">{value.body}</p>
                    </div>
                    {value.canDelete && (
                      <IconButton
                        title="댓글 삭제"
                        disabled={busy}
                        onClick={() => run(
                          () => api.delete(`${apiRoot}/workspaces/${rootOrgId}/files/${file.id}/comments/${value.id}`),
                          '댓글을 삭제했습니다.',
                        )}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </IconButton>
                    )}
                  </li>
                ))}
                {!details?.comments?.length && (
                  <li className="flex min-h-40 items-center justify-center text-sm text-slate-500">등록된 댓글이 없습니다.</li>
                )}
              </ol>
              {details?.canComment && (
                <form onSubmit={addComment} className="border-t border-slate-200 pt-3">
                  {!!mentionedMembers.length && (
                    <div className="mb-2 flex flex-wrap gap-1">
                      {mentionedMembers.map((member) => (
                        <button
                          type="button"
                          key={member.principalId}
                          onClick={() => setMentionedIds((previous) => previous.filter((id) => id !== member.principalId))}
                          className="inline-flex items-center gap-1 rounded-full bg-cyan-50 px-2 py-1 text-xs text-cyan-800"
                        >
                          @{member.label}<X className="h-3 w-3" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      rows={3}
                      maxLength={2000}
                      placeholder="댓글 작성"
                      className="min-h-20 min-w-0 flex-1 resize-y rounded-md border border-slate-300 p-3 text-sm"
                    />
                    <div className="flex flex-col gap-1">
                      <select
                        aria-label="언급할 조직원"
                        value={mentionTarget}
                        onChange={(event) => {
                          const value = Number(event.target.value)
                          if (value) setMentionedIds((previous) => [...new Set([...previous, value])])
                          setMentionTarget('')
                        }}
                        className="h-9 w-36 rounded-md border border-slate-300 bg-white px-2 text-xs"
                      >
                        <option value="">@ 조직원</option>
                        {members.filter((member) => !mentionedIds.includes(member.principalId)).map((member) => (
                          <option key={member.principalId} value={member.principalId}>{member.label}</option>
                        ))}
                      </select>
                      <button
                        type="submit"
                        disabled={busy || !comment.trim()}
                        className="inline-flex h-9 items-center justify-center gap-1 rounded-md bg-sgez-primary px-3 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        <Send className="h-4 w-4" />등록
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          ) : activeTab === 'TAGS' ? (
            <div>
              <label className="block text-sm font-medium text-slate-700">
                태그
                <input
                  value={tagText}
                  onChange={(event) => setTagText(event.target.value)}
                  disabled={!details?.canManage}
                  placeholder="쉼표로 구분"
                  className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50"
                />
              </label>
              {details?.canManage && (
                <button type="button" onClick={saveTags} disabled={busy} className="mt-3 h-9 rounded-md bg-sgez-primary px-4 text-sm font-semibold text-white disabled:opacity-50">저장</button>
              )}
            </div>
          ) : activeTab === 'REVIEW' ? (
            <div className="space-y-5">
              {details?.review ? (
                <section className="border-y border-slate-200 py-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{REVIEW_LABELS[details.review.status] || '검토'}</p>
                      <p className="mt-1 text-sm text-slate-600">{details.review.assignedToName} · 요청자 {details.review.requestedByName}</p>
                    </div>
                    {details.review.status === 'APPROVED' && <Check className="h-6 w-6 text-emerald-600" />}
                  </div>
                  {details.review.requestMessage && <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{details.review.requestMessage}</p>}
                  {details.review.decisionMessage && <p className="mt-3 border-l-2 border-slate-300 pl-3 text-sm text-slate-600">{details.review.decisionMessage}</p>}
                </section>
              ) : (
                <p className="text-sm text-slate-500">진행 중인 검토가 없습니다.</p>
              )}

              {details?.canDecideReview && (
                <section>
                  <textarea
                    value={decisionMessage}
                    onChange={(event) => setDecisionMessage(event.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="검토 의견"
                    className="w-full resize-y rounded-md border border-slate-300 p-3 text-sm"
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <button type="button" onClick={() => decide('REJECTED')} disabled={busy} className="h-9 rounded-md border border-red-200 px-4 text-sm font-medium text-red-700">반려</button>
                    <button type="button" onClick={() => decide('APPROVED')} disabled={busy} className="h-9 rounded-md bg-emerald-600 px-4 text-sm font-semibold text-white">승인</button>
                  </div>
                </section>
              )}

              {details?.canManage && details?.review?.status !== 'PENDING' && (
                <section className="grid gap-2 sm:grid-cols-[200px_minmax(0,1fr)_auto]">
                  <select value={reviewTarget} onChange={(event) => setReviewTarget(event.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm">
                    <option value="">검토 담당자</option>
                    {members.map((member) => <option key={member.principalId} value={member.principalId}>{member.label}</option>)}
                  </select>
                  <input value={reviewMessage} onChange={(event) => setReviewMessage(event.target.value)} maxLength={500} placeholder="요청 메시지" className="h-10 min-w-0 rounded-md border border-slate-300 px-3 text-sm" />
                  <button type="button" onClick={requestReview} disabled={busy || !reviewTarget} className="h-10 rounded-md bg-sgez-primary px-4 text-sm font-semibold text-white disabled:opacity-50">검토 요청</button>
                </section>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-200 border-y border-slate-200">
              <div className="flex items-center justify-between py-4">
                <span className="text-sm text-slate-600">파일 안전 검사</span>
                <strong className={`text-sm ${details?.scanStatus === 'THREAT' ? 'text-red-700' : 'text-slate-800'}`}>
                  {SCAN_LABELS[details?.scanStatus] || '확인 대기'}
                </strong>
              </div>
              <div className="flex items-center justify-between py-4">
                <span className="text-sm text-slate-600">민감정보 확인</span>
                <strong className={`text-sm ${['DETECTED', 'SENSITIVE'].includes(details?.sensitivityStatus) ? 'text-amber-700' : 'text-slate-800'}`}>
                  {SECURITY_LABELS[details?.sensitivityStatus] || '확인 전'}
                </strong>
              </div>
              {(details?.sensitivityReasons || []).map((reason) => (
                <div key={reason} className="py-3 text-sm text-amber-700">
                  {reason === 'RESIDENT_NUMBER' ? '주민등록번호 형식 확인 필요'
                    : reason === 'PHONE_NUMBER' ? '전화번호 형식 확인 필요'
                      : reason === 'ACCOUNT_NUMBER' ? '계좌번호 형식 확인 필요'
                        : '민감정보 확인 필요'}
                </div>
              ))}
            </div>
          )}
        </DialogBody>
      </DialogPanel>
    </DialogBackdrop>
  )
}
